/**
 * Chat Message List Component
 *
 * Displays all messages in a chat session with proper formatting and styling.
 * Features:
 * - Markdown rendering with syntax highlighting
 * - File attachment display
 * - Message status indicators
 * - Warte-Blase mit Punkten, solange KIAN noch nichts geschrieben hat
 * - Error boundaries
 * - Mobile responsive design
 */

import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { Box, Stack, Text, Group, Avatar, Paper, Button, ActionIcon, Tooltip, Badge, Kbd } from '@mantine/core';
import { IconRobot, IconUser, IconRefresh, IconCheck, IconAlertCircle, IconCopy, IconCopyCheck, IconCoins, IconBolt, IconMicrophone, IconVolume } from '@tabler/icons-react';
import { ChatMessageListProps, ChatMessageWithStatus } from '../../types/assistant';
import { RichMessageRenderer } from './RichMessageRenderer';
import { FileAttachment } from './FileAttachment';
import { ScreenshotPreview } from './ScreenshotPreview';
import { TypingIndicator } from './TypingIndicator';
import { ChatErrorBoundary } from './ChatErrorBoundary';
import { FeedbackButtons } from './FeedbackButtons';
import { KianQuickPrompts } from './flyout/KianQuickPrompts';
import { KianActionCards } from './actions/KianActionCard';
import { ToolBudgetNotice } from './ToolBudgetNotice';
import styles from './ChatMessageList.module.css';

/** IDs der Entwürfe, die zu einer KIAN-Antwort gehören (metadata.actionIds). */
function actionIdsOf(metadata: Record<string, unknown> | undefined): string[] {
  const ids = metadata?.actionIds;
  return Array.isArray(ids) ? ids.filter((v): v is string => typeof v === 'string') : [];
}

function calendarDayKey(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Uhrzeit nur bei „heute“; sonst Datum + Uhrzeit (de-DE). */
function formatMessageTimestamp(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (calendarDayKey(d) === calendarDayKey(now)) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Zentrierte Tagesmarke zwischen Nachrichten unterschiedlichen Kalendertags. */
function formatDaySeparatorLabel(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (calendarDayKey(d) === calendarDayKey(now)) {
    return 'Heute';
  }
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  if (calendarDayKey(d) === calendarDayKey(yest)) {
    return 'Gestern';
  }
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Eine Antwort, die läuft, aber noch kein Wort hat: Blase mit Punkten statt leerer Blase. */
function isAwaitingFirstWord(message: { role: string; content?: string; metadata?: Record<string, unknown> }): boolean {
  return message.role === 'ASSISTANT' && message.metadata?.streaming === true && !message.content?.trim();
}

/** KIAN-Avatar + Blase mit Punkten — für Antworten ohne Stream-Blase (Datei-Upload). */
function KianWaitingBubble() {
  return (
    <Group align="flex-start" gap="sm" className={styles.messageGroup} data-role="ASSISTANT">
      <Avatar size="sm" radius="xl" color="cyberOrange" className={styles.avatar} style={{ flexShrink: 0 }}>
        <IconRobot size={18} />
      </Avatar>
      <Paper px="md" py={6} radius="md" className={styles.messageBubble} data-role="ASSISTANT" aria-label="KIAN antwortet">
        <TypingIndicator isVisible={true} variant="dots" color="cyberOrange" />
      </Paper>
    </Group>
  );
}

export function ChatMessageList({
  messages,
  isLoading = false,
  currentUserId,
  onRetryMessage,
  onRegenerateMessage,
  onRegenerateDeepMessage,
  sessionId,
  entityType = null,
  isFullscreen = false,
  readOnly = false,
  toolBudget,
  canRaiseToolBudget = false,
  onRaiseToolBudget,
}: ChatMessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);
  const scrollRafRef = useRef<number | null>(null);
  const [retryingMessageIds, setRetryingMessageIds] = React.useState<Set<string>>(new Set());
  const [regeneratingIds, setRegeneratingIds] = useState<Set<string>>(new Set());
  const [regeneratingDeepIds, setRegeneratingDeepIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleCopy = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      window.setTimeout(() => {
        setCopiedId((prev) => (prev === messageId ? null : prev));
      }, 1500);
    } catch {
      // Clipboard-API kann fehlschlagen (z.B. non-HTTPS). Silent fail ist OK.
    }
  };

  const handleRegenerate = async (messageId: string) => {
    if (!onRegenerateMessage) return;
    try {
      setRegeneratingIds((prev) => new Set(prev).add(messageId));
      await onRegenerateMessage(messageId);
    } catch (error) {
      console.error('Failed to regenerate message:', error);
    } finally {
      setRegeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  const handleRegenerateDeep = async (messageId: string) => {
    if (!onRegenerateDeepMessage) return;
    try {
      setRegeneratingDeepIds((prev) => new Set(prev).add(messageId));
      await onRegenerateDeepMessage(messageId);
    } catch (error) {
      console.error('Failed to regenerate with deep model:', error);
    } finally {
      setRegeneratingDeepIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  // Überarbeitet KIAN einen Entwurf (Anleitung), hängt er an mehreren Antworten — die Karte steht
  // nur unter der neuesten, darüber ein Verweis.
  const latestMessageOfAction = useMemo(() => {
    const latest = new Map<string, string>();
    for (const m of messages) {
      if (m.role !== 'ASSISTANT') continue;
      for (const id of actionIdsOf(m.metadata)) latest.set(id, m.id);
    }
    return latest;
  }, [messages]);
  const supersededFor = useCallback(
    (messageId: string, ids: string[]): Set<string> | undefined => {
      const out = ids.filter((id) => latestMessageOfAction.get(id) !== messageId);
      return out.length ? new Set(out) : undefined;
    },
    [latestMessageOfAction],
  );

  // Budget-Hinweis nur an der neuesten Antwort — ältere sind durch, neu erzeugt wird nur die letzte
  const latestAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'ASSISTANT') return messages[i].id;
    }
    return null;
  }, [messages]);

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastMessageContentLength = lastMessage?.content?.length ?? 0;
  const lastMessageId = lastMessage?.id ?? null;
  const lastMessageRole = lastMessage?.role ?? null;

  useEffect(() => {
    if (lastMessageRole === 'USER') {
      pinnedToBottomRef.current = true;
    }
  }, [lastMessageId, lastMessageRole]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (!pinnedToBottomRef.current) return;
    // Cancel any queued scroll — streaming deltas would otherwise stack RAFs.
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = scrollRef.current;
      if (el && pinnedToBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    return () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [messages.length, lastMessageId, lastMessageContentLength, isLoading]);

  const handleRetryMessage = async (messageId: string) => {
    if (!onRetryMessage) return;

    try {
      setRetryingMessageIds((prev) => new Set(prev).add(messageId));
      await onRetryMessage(messageId);
    } catch (error) {
      console.error('Failed to retry message:', error);
    } finally {
      setRetryingMessageIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  // Memoize message list rendering for performance
  const messageElements = useMemo(() => {
    return messages.flatMap((message, index) => {
      const messageWithStatus = message as ChatMessageWithStatus;
      const isRetrying = retryingMessageIds.has(message.id);
      const isFailed = messageWithStatus.status === 'failed';
      const isSending = messageWithStatus.status === 'sending';
      const awaiting = isAwaitingFirstWord(message);

      const prev = index > 0 ? messages[index - 1] : null;
      const showDaySeparator =
        prev != null &&
        calendarDayKey(prev.createdAt) !== calendarDayKey(message.createdAt);

      const daySep = showDaySeparator ? (
        <Box key={`day-sep-${message.id}`} className={styles.daySeparator} component="div" role="separator">
          <Text size="xs" c="dimmed" fw={500}>
            {formatDaySeparatorLabel(message.createdAt)}
          </Text>
        </Box>
      ) : null;

      const bubble = (
        <ChatErrorBoundary key={message.id}>
          <Group
            align="flex-start"
            gap="sm"
            className={styles.messageGroup}
            data-role={message.role}
            style={{
              justifyContent: message.role === 'USER' ? 'flex-end' : 'flex-start',
              opacity: isSending ? 0.7 : 1,
            }}
          >
            {message.role === 'ASSISTANT' && (
              <Avatar
                size="sm"
                radius="xl"
                color="cyberOrange"
                className={styles.avatar}
                style={{ flexShrink: 0 }}
              >
                <IconRobot size={18} />
              </Avatar>
            )}

            <Box
              className={styles.messageContainer}
              // Antworten mit Entwurfskarte dürfen die volle Breite nutzen — eine Ticketliste in
              // 75 % eines schmalen Flyouts ist nicht mehr lesbar.
              style={
                message.role === 'ASSISTANT' && actionIdsOf(message.metadata).length > 0
                  ? { maxWidth: '100%', flex: 1, minWidth: 0 }
                  : { maxWidth: isFullscreen ? '80%' : '75%' }
              }
            >
              <Paper
                {...(awaiting ? { px: 'md', py: 6 } : { p: 'md' })}
                radius="md"
                className={styles.messageBubble}
                data-role={message.role}
                aria-label={awaiting ? 'KIAN antwortet' : undefined}
              >
                {/* Message Content */}
                <Box className={styles.messageContent}>
                  {awaiting ? (
                    <TypingIndicator isVisible={true} variant="dots" color="cyberOrange" />
                  ) : message.role === 'ASSISTANT' ? (
                    <RichMessageRenderer content={message.content} role="ASSISTANT" />
                  ) : (
                    <Text
                      size="sm"
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.content}
                    </Text>
                  )}
                </Box>

                {/* File Attachments */}
                {messageWithStatus.attachments && messageWithStatus.attachments.length > 0 && (
                  <Stack gap="xs" mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-dark-4)' }}>
                    {/* Screenshots - rendered inline with preview */}
                    {messageWithStatus.attachments.filter((a) => a.mimeType.startsWith('image/')).map((attachment) => (
                      <ScreenshotPreview
                        key={attachment.id}
                        src={attachment.url}
                        alt={attachment.filename}
                        filename={attachment.filename}
                        thumbnailSize={200}
                      />
                    ))}

                    {/* Other files - shown as attachment list */}
                    {messageWithStatus.attachments.filter((a) => !a.mimeType.startsWith('image/')).map((attachment) => (
                      <FileAttachment
                        key={attachment.id}
                        file={{
                          id: attachment.id,
                          name: attachment.filename,
                          type: attachment.mimeType,
                          size: attachment.size,
                          url: attachment.url,
                        }}
                      />
                    ))}
                  </Stack>
                )}

                {/* Metadata — nicht an der Warte-Blase: eine Uhrzeit ohne Text sieht nach leerer Antwort aus */}
                {!awaiting && (
                <Group justify="space-between" align="center" mt="md" gap="xs" wrap="wrap">
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Text
                      size="xs"
                      ff="monospace"
                      c={message.role === 'USER' ? 'rgba(255,255,255,0.75)' : 'dimmed'}
                      style={{ letterSpacing: '0.02em' }}
                    >
                      {formatMessageTimestamp(message.createdAt)}
                    </Text>
                    {/* Gesprächsmodus: gesprochen bzw. vorgelesen — der Chat ist das Protokoll */}
                    {message.metadata?.voice === true ? (
                      <Tooltip label={message.role === 'USER' ? 'Gesprochen (Gesprächsmodus)' : 'Vorgelesen (Gesprächsmodus)'} withArrow>
                        {message.role === 'USER' ? (
                          <IconMicrophone size={12} color="rgba(255,255,255,0.75)" aria-label="Gesprochen" />
                        ) : (
                          <IconVolume size={12} color="var(--mantine-color-dimmed)" aria-label="Vorgelesen" />
                        )}
                      </Tooltip>
                    ) : null}
                    {message.role === 'ASSISTANT' && typeof message.metadata?.model === 'string' && (
                      <Tooltip label="Verwendetes Modell" withArrow>
                        <Badge
                          size="xs"
                          variant="light"
                          color="gray"
                          styles={{ label: { fontFamily: 'var(--mantine-font-family-monospace)', letterSpacing: '0.02em' } }}
                        >
                          {message.metadata.model as string}
                        </Badge>
                      </Tooltip>
                    )}
                    {message.role === 'ASSISTANT' &&
                      typeof message.metadata?.inputTokens === 'number' &&
                      typeof message.metadata?.outputTokens === 'number' &&
                      !message.metadata?.streaming && (() => {
                        const inTok = message.metadata.inputTokens as number;
                        const outTok = message.metadata.outputTokens as number;
                        const costEur = message.metadata.costEur as number | undefined;
                        const tier = message.metadata.tier as string | undefined;
                        // Gemessen seit v1.386.0: Summe aller Aufrufe der Tool-Schleife; fehlt bei geschätzten Werten
                        const llmCalls = typeof message.metadata.llmCalls === 'number' ? message.metadata.llmCalls : null;
                        const fmtK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
                        const costStr = costEur != null
                          ? costEur === 0
                            ? '€0.00'
                            : costEur < 0.00001
                              ? `<€0.00001`
                              : `~€${costEur.toFixed(5).replace(/0+$/, '').replace(/\.$/, '')}`
                          : null;
                        return (
                          <Tooltip
                            label={`↑ ${inTok.toLocaleString()} Input-Tokens · ↓ ${outTok.toLocaleString()} Output-Tokens${costStr ? ` · ${costStr}` : ''}${tier ? ` · Tier: ${tier}` : ''}${llmCalls != null && llmCalls > 1 ? ` · ${llmCalls} KIAN-Aufrufe zusammengezählt` : ''}${llmCalls == null ? ' · geschätzt' : ''}`}
                            withArrow
                            multiline
                            w={300}
                          >
                            <Badge
                              size="xs"
                              variant="dot"
                              color={tier === 'deep' ? 'orange' : 'teal'}
                              leftSection={<IconCoins size={9} />}
                              styles={{ label: { fontFamily: 'var(--mantine-font-family-monospace)', letterSpacing: '0.02em' } }}
                            >
                              {fmtK(inTok)}↑ {fmtK(outTok)}↓{costStr ? ` ${costStr}` : ''}
                            </Badge>
                          </Tooltip>
                        );
                      })()
                    }
                  </Group>

                  <Group gap="xs" wrap="nowrap">
                    {message.role === 'ASSISTANT' && sessionId && message.content?.trim() && (
                      <FeedbackButtons messageId={message.id} sessionId={sessionId} />
                    )}

                    {/* Copy + Regenerate für Assistant-Messages (nur wenn Content vorhanden und nicht mehr streamend) */}
                    {message.role === 'ASSISTANT' && message.content?.trim() && !message.metadata?.streaming && (
                      <>
                        <Tooltip label={copiedId === message.id ? 'Kopiert!' : 'Kopieren'} withArrow>
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color={copiedId === message.id ? 'green' : 'gray'}
                            onClick={() => void handleCopy(message.id, message.content)}
                            aria-label="Nachricht kopieren"
                          >
                            {copiedId === message.id ? <IconCopyCheck size={14} /> : <IconCopy size={14} />}
                          </ActionIcon>
                        </Tooltip>
                        {onRegenerateMessage && (
                          <Tooltip label="Neue Antwort generieren" withArrow>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="gray"
                              loading={regeneratingIds.has(message.id)}
                              onClick={() => void handleRegenerate(message.id)}
                              aria-label="Antwort regenerieren"
                            >
                              <IconRefresh size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {onRegenerateDeepMessage && (
                          <Tooltip label="Mit PowerMode neu generieren (tiefstes Modell)" withArrow>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="orange"
                              loading={regeneratingDeepIds.has(message.id)}
                              onClick={() => void handleRegenerateDeep(message.id)}
                              aria-label="Mit PowerMode regenerieren"
                            >
                              <IconBolt size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </>
                    )}

                    {/* Status Indicators */}
                    {message.role === 'USER' && (
                      <Group gap={4} wrap="nowrap">
                        {isSending && (
                          <Tooltip label="Sendet..." withArrow>
                            <Badge size="xs" variant="light">
                              Sendet...
                            </Badge>
                          </Tooltip>
                        )}
                        {isFailed && (
                          <Tooltip label="Senden fehlgeschlagen" withArrow>
                            <Badge size="xs" color="red" variant="light" leftSection={<IconAlertCircle size={10} />}>
                              Fehlgeschlagen
                            </Badge>
                          </Tooltip>
                        )}
                        {messageWithStatus.status === 'sent' && (
                          <Tooltip label="Gesendet" withArrow>
                            <ActionIcon size="xs" color="gray" variant="transparent">
                              <IconCheck size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    )}
                  </Group>
                </Group>
                )}

                {/* Error Message */}
                {isFailed && messageWithStatus.error && (
                  <Text size="xs" c="red" mt="xs">
                    {messageWithStatus.error}
                  </Text>
                )}
              </Paper>

              {/* Von KIAN vorbereitete Entwürfe (Tickets anlegen/ändern, Import) — erst die
                  Bestätigung in der Karte schreibt nach weclapp. */}
              {message.role === 'ASSISTANT' && actionIdsOf(message.metadata).length > 0 && (
                <KianActionCards
                  actionIds={actionIdsOf(message.metadata)}
                  compact={!isFullscreen}
                  readOnly={readOnly}
                  superseded={supersededFor(message.id, actionIdsOf(message.metadata))}
                />
              )}

              {message.role === 'ASSISTANT' &&
                message.id === latestAssistantId &&
                message.metadata?.toolBudgetExhausted === true &&
                !message.metadata?.streaming && (
                  <ToolBudgetNotice
                    usedBudget={typeof message.metadata.toolBudget === 'number' ? message.metadata.toolBudget : null}
                    toolBudget={toolBudget}
                    canRaise={canRaiseToolBudget}
                    readOnly={readOnly || isLoading}
                    onRaise={onRaiseToolBudget ? (perMessage) => onRaiseToolBudget(message.id, perMessage) : undefined}
                  />
                )}

              {/* Retry Button */}
              {isFailed && onRetryMessage && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleRetryMessage(message.id)}
                  loading={isRetrying}
                  leftSection={<IconRefresh size={14} />}
                  style={{ marginTop: '0.5rem' }}
                >
                  Erneut versuchen
                </Button>
              )}

              {/* Retry Count */}
              {messageWithStatus.retryCount && messageWithStatus.retryCount > 0 && (
                <Text size="xs" c="dimmed" mt="xs">
                  Versuch {messageWithStatus.retryCount}
                </Text>
              )}
            </Box>

            {message.role === 'USER' && (
              <Avatar
                size="sm"
                radius="xl"
                color="gray"
                className={styles.avatar}
                style={{ flexShrink: 0 }}
              >
                <IconUser size={18} />
              </Avatar>
            )}
          </Group>
        </ChatErrorBoundary>
      );

      return daySep ? [daySep, bubble] : [bubble];
    });
  }, [messages, retryingMessageIds, onRetryMessage, onRegenerateMessage, onRegenerateDeepMessage, regeneratingIds, regeneratingDeepIds, copiedId, sessionId, isFullscreen, readOnly, supersededFor, latestAssistantId, toolBudget, canRaiseToolBudget, onRaiseToolBudget, isLoading]);

  return (
    <Box
      ref={scrollRef}
      className={styles.messageList}
      onScroll={handleScroll}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: 'var(--mantine-spacing-md)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {messages.length === 0 && !isLoading ? (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--mantine-spacing-md)',
            height: '100%',
            minHeight: '200px',
          }}
        >
          <Text c="dimmed" ta="center" size="sm">
            Schreibe deine erste Nachricht, um das Gespräch zu starten.
          </Text>
          <KianQuickPrompts entityType={entityType} variant="inline" />
          <Text c="dimmed" ta="center" size="xs">
            Tipp: Tippe <Kbd>/</Kbd> für schnelle Befehle
          </Text>
        </Box>
      ) : (
        <Stack gap="md" style={{ flex: 1 }}>
          {messageElements}

          {/* Warten auf KIAN — beim Streamen trägt die Antwort-Blase selbst die Punkte */}
          {isLoading && lastMessage?.metadata?.streaming !== true && (
            <KianWaitingBubble />
          )}
        </Stack>
      )}
    </Box>
  );
}
